import { useCallback, useEffect, useMemo, useState } from 'react';
import { CollectionView } from 'common';
import { SortBy, sortComparators } from '@src/pages/Collection/List/consts';
import { CollectionsListState } from '@src/types/Collection';
import { buildCollectionsList } from './utils/buildCollectionsList';
import { areCategoryListsEqual } from './utils/areCategoryListsEqual';
import { getCollectionIdToCategoryMap } from './utils/getCollectionIdToCategoryMap';
import { getVisibleCollectionsByCategory } from './utils/getVisibleCollectionsByCategory';

interface UseCollectionsListStateArgs {
  displayedCollections: CollectionView[];
  filteredCollections: CollectionView[];
  sortBy: SortBy;
}

export function useCollectionsListState({
  displayedCollections,
  filteredCollections,
  sortBy,
}: UseCollectionsListStateArgs) {
  const sortMethod = sortComparators[sortBy];
  const computedCollectionsList = useMemo(
    () => buildCollectionsList(displayedCollections, sortMethod),
    [displayedCollections, sortMethod]
  );

  const [collectionsList, setCollectionsList] = useState<CollectionsListState>(computedCollectionsList);

  useEffect(() => {
    setCollectionsList((prev) =>
      areCategoryListsEqual(prev, computedCollectionsList) ? prev : computedCollectionsList
    );
  }, [computedCollectionsList]);

  const collectionIdToCategory = useMemo(
    () => getCollectionIdToCategoryMap(collectionsList),
    [collectionsList]
  );

  const visibleCollectionsByCategory = useMemo(
    () => getVisibleCollectionsByCategory(collectionsList, filteredCollections),
    [collectionsList, filteredCollections]
  );

  const findCategoryForCollection = useCallback(
    (collectionId: string) => collectionIdToCategory.get(collectionId),
    [collectionIdToCategory]
  );

  return {
    collectionsList,
    setCollectionsList,
    visibleCollectionsByCategory,
    findCategoryForCollection,
  };
}
